import orderBy from 'lodash/orderBy';

function addNotifications(current, newNotifications) {
  return newNotifications
    .reduce((acc, notification) => {
      // skip duplicate notifications, notifications are duplicates if they have the same slug
      if (acc.find((n) => n.slug === notification.slug)) {
        return acc;
      }

      return acc.concat(notification);
    }, current)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

function sortGroupedNotifications(groupedNotifications, field, order = 'asc') {
  return orderBy(groupedNotifications, [field], [order]);
}

function groupNotifications(notifications, followedThemeIds) {
  return notifications.reduce((acc, notification) => {
    /** Group theme items */
    const followedThemesFilter = notification?.themes?.filter((theme) =>
      followedThemeIds?.includes(theme?.id)
    );

    const firstFollowedTheme = followedThemesFilter
      ? followedThemesFilter[0]
      : null;

    // First followed theme can be a parent theme or a subtheme
    const themeGroupName =
      firstFollowedTheme?.parent?.name || firstFollowedTheme?.name;

    let themeNotifications = acc.find(
      (themes) => themes?.name === themeGroupName
    );

    if (themeNotifications === undefined) {
      if (themeGroupName) {
        themeNotifications = {
          themeId: firstFollowedTheme?.parent?.id || firstFollowedTheme?.id,
          name: themeGroupName,
          notifications: [],
          unreadCount: 0,
          themeOrder:
            firstFollowedTheme?.parent?.order || firstFollowedTheme?.order,
          themeSlug:
            firstFollowedTheme?.parent?.slug || firstFollowedTheme?.slug,
          themeColor:
            firstFollowedTheme?.parent?.color || firstFollowedTheme?.color,
          type: 'theme',
        };

        acc.push(themeNotifications);
      }
    }

    if (themeNotifications) {
      const notificationWithFilteredThemes = {
        ...notification,
        themes: followedThemesFilter,
      };
      themeNotifications.notifications.push(notificationWithFilteredThemes);
      themeNotifications.unreadCount += notificationWithFilteredThemes.isRead
        ? 0
        : 1;
    }

    /** Group brand items */
    const brandName = notification.brand.name;
    let brandNotifications = acc.find((brand) => brand.name === brandName);

    const hasThemes = notification?.themes && notification?.themes?.length > 0;

    if (!hasThemes && brandNotifications === undefined) {
      if (brandName) {
        brandNotifications = {
          name: brandName,
          notifications: [],
          unreadCount: 0,
          type: 'brand',
        };
        acc.push(brandNotifications);
      }
    }

    if (brandNotifications) {
      const notificationExistsInTheme = themeNotifications?.notifications?.find(
        (item) => item.slug === notification.slug
      );
      if (notificationExistsInTheme) {
        return acc;
      }

      if (!hasThemes) {
        brandNotifications.notifications.push(notification);
        brandNotifications.unreadCount += notification.isRead ? 0 : 1;
      }
    }

    const groupsWithNotifications = acc.filter(
      (item) => item.notifications.length
    );

    return groupsWithNotifications;
  }, []);
}

function isUpdated(current, newNotifications) {
  return newNotifications.length > current.length;
}

function unreadCount(notifications) {
  return notifications.filter((notification) => !notification.isRead).length;
}

export function reducer(state, action) {
  let notifications;

  switch (action.type) {
    case 'ADD_NOTIFICATIONS':
      notifications = addNotifications(
        state.notifications,
        Array.isArray(action.payload) ? action.payload : [action.payload]
      );
      return {
        ...state,
        notifications,
        lastUpdate: isUpdated(state.notifications, notifications)
          ? Date.now()
          : state.lastUpdate,
        groupedNotifications: sortGroupedNotifications(
          groupNotifications(notifications, state.followedThemeIds),
          'themeOrder'
        ),
        unreadCount: unreadCount(notifications),
      };
    case 'UPDATE_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        groupedNotifications: sortGroupedNotifications(
          groupNotifications(action.payload, state.followedThemeIds),
          'themeOrder'
        ),
        lastUpdate: isUpdated(state.notifications, action.payload)
          ? Date.now()
          : state.lastUpdate,
        unreadCount: unreadCount(action.payload),
      };
    case 'SET_FOLLOWED_THEMES':
      return {
        ...state,
        followedThemeIds: action.payload,
      };
    case 'RESET':
      return {
        notifications: [],
        groupedNotifications: [],
        lastUpdate: Date.now(),
        unreadCount: 0,
      };
    default:
      throw new Error(`Reducer: no case for "${action.type}"`);
  }
}
